import { PieChart } from '@/common/dashboard/charts/PieChart';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { Grid } from '@/common/grid/Grid';
import { Spacer } from '@/common/layout/SpacerLayout';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Box, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

export const AbvOpenPeriodDiscrepanciesGridChart = ({ prodIds, breakoutByProduct = false }) => {
  const rsrc = useRsrc();
  const [data, setData] = useState([]);
  const [pieFilter, setPieFilter] = useState(null);
  const [isGridReady, setIsGridReady] = useState(false);
  const gridRef = useRef();
  const theme = useTheme();
  const prodIdsKey = useMemo(() => JSON.stringify(prodIds ?? []), [prodIds]);

  const fieldDefs = useMemo(() => [
    ...(breakoutByProduct ? [{ field: 'prodName', displayName: 'Product' }] : []),
    { field: 'channelName', displayName: 'Channel' },
    { field: 'type', displayName: 'Type' },
    { field: 'check', displayName: '', type: 'BOOLEAN', maxWidth: 100 },
    { field: 'bsAmt', displayName: 'BS Amount', type: 'NUMBER', decimalPrecision: 2 },
    { field: 'plAmt', displayName: 'P/L Amount', type: 'NUMBER', decimalPrecision: 2 },
    
  ], [breakoutByProduct]);

  const gridKey = useMemo(() => `${breakoutByProduct}-${JSON.stringify(fieldDefs)}`, [breakoutByProduct, fieldDefs]);

  useEffect(() => {
    setIsGridReady(false);
  }, [gridKey]);

  useEffect(() => {
    let isActive = true;
    setData([]);
    setPieFilter(null);

    const params = { prodIds: [...(prodIds ?? [])], breakoutByProduct };
    rsrc.data('gtn', 'RECON_OPEN_PERIOD_DISCREPANCIES_DATA_PLUGIN', params).then(res => {
      if (!isActive) {
        return;
      }

      const rows = (Array.isArray(res) ? res : []).map((row) => ({
        ...row,
        bsAmt: Number(row.bsAmt),
        plAmt: Number(row.plAmt),
        check: row.check === true
      }));

      setData(rows);
    });

    return () => {
      isActive = false;
    };
  }, [breakoutByProduct, prodIdsKey, rsrc]);

  useEffect(() => {
    if (isGridReady && gridRef.current) {
      const rows = pieFilter === 'match'
        ? data.filter(row => row.check)
        : pieFilter === 'mismatch'
          ? data.filter(row => !row.check)
          : data;

      gridRef.current.setDataSource(rows);
    }
  }, [data, isGridReady, pieFilter]);

  const onGridReady = () => {
    setIsGridReady(true);
  };

  const matchCount = useMemo(() => data.filter(row => row.check).length, [data]);
  const mismatchCount = useMemo(() => data.filter(row => !row.check).length, [data]);
  const filteredRows = useMemo(() => {
    if (pieFilter === 'match') {
      return data.filter(row => row.check);
    }

    if (pieFilter === 'mismatch') {
      return data.filter(row => !row.check);
    }

    return data;
  }, [data, pieFilter]);

  const pieData = useMemo(() => [
    { label: 'Match', value: matchCount, color: theme.palette.success.main },
    { label: 'Mismatch', value: mismatchCount, color: theme.palette.error.main },
  ], [mismatchCount, matchCount, theme.palette.error.main, theme.palette.success.main]);

  const handlePieClick = (point) => {
    if (point?.label === 'Match') {
      setPieFilter(prev => prev === 'match' ? null : 'match');
      return;
    }

    if (point?.label === 'Mismatch') {
      setPieFilter(prev => prev === 'mismatch' ? null : 'mismatch');
    }
  };

  return (
    <Spacer horizontal weights={[5, 10]}>
      <DashboardCard title="Open Period Summary">
        <Box sx={{ height: '100%', minHeight: 225 }}>
          <PieChart
            data={pieData}
            legend="bottom"
            pieSize="96%"
            centerLabel={String(filteredRows.length)}
            centerSubLabel={pieFilter == null ? 'Total Rows' : `${pieFilter === 'match' ? 'Match' : 'Mismatch'} Only`}
            onClick={handlePieClick}
          />
        </Box>
      </DashboardCard>

      <DashboardCard title="Open Period Discrepancies">
        <Box sx={{ height: '100%', minHeight: 0, pt: 1 }}>
            <Grid
              key={gridKey}
              ref={gridRef}
              gridOptions={{
                fieldDefs,
                rowModelType: 'clientSide',
                statusBar: null,
              }}
              events={{
                onGridReady,
              }}
            />
          </Box>
      </DashboardCard>
    </Spacer>
  );
};